import { CockpitContractError } from './cockpit-helpers';
import {
  MANAGEMENT_TIMELINE_DEFAULT_LIMIT,
  MANAGEMENT_TIMELINE_DEFAULT_PAGE,
  MANAGEMENT_TIMELINE_DEFAULT_VIEW,
  MANAGEMENT_TIMELINE_GROUP_BY_VALUES,
  MANAGEMENT_TIMELINE_MARKER_TYPE_VALUES,
  MANAGEMENT_TIMELINE_MAX_LIMIT,
  MANAGEMENT_TIMELINE_OUTCOME_VALUES,
  MANAGEMENT_TIMELINE_VIEW_VALUES,
  type ManagementTimelineGroupBy,
  type ManagementTimelineQuery,
} from './management-timeline';

export type ManagementTimelineQueryInput = Record<string, unknown>;
export type SerializedManagementTimelineQuery = Record<string, string>;

export function parseManagementTimelineQuery(
  input: ManagementTimelineQueryInput | ManagementTimelineQuery,
): ManagementTimelineQuery {
  return {
    ...parseFilters(input),
    page: positiveInteger(
      input.page,
      'page',
      MANAGEMENT_TIMELINE_DEFAULT_PAGE,
      Number.MAX_SAFE_INTEGER,
    ),
    limit: positiveInteger(
      input.limit,
      'limit',
      MANAGEMENT_TIMELINE_DEFAULT_LIMIT,
      MANAGEMENT_TIMELINE_MAX_LIMIT,
    ),
  };
}

function parseFilters(
  input: ManagementTimelineQueryInput | ManagementTimelineQuery,
): ManagementTimelineQuery {
  const from = optionalTimestamp(input.from, 'from');
  const to = optionalTimestamp(input.to, 'to');
  if (from && to && Date.parse(from) > Date.parse(to)) invalid('period');

  return {
    view:
      optionalChoice(input.view, MANAGEMENT_TIMELINE_VIEW_VALUES, 'view') ??
      MANAGEMENT_TIMELINE_DEFAULT_VIEW,
    ...optionalStringField(input, 'participant'),
    ...optionalStringField(input, 'skill'),
    ...optionalStringField(input, 'task'),
    ...optionalTypedField(
      'outcome',
      optionalChoice(
        input.outcome,
        MANAGEMENT_TIMELINE_OUTCOME_VALUES,
        'outcome',
      ),
    ),
    ...optionalTypedField(
      'type',
      optionalChoice(
        input.type,
        MANAGEMENT_TIMELINE_MARKER_TYPE_VALUES,
        'type',
      ),
    ),
    ...optionalTypedField('from', from),
    ...optionalTypedField('to', to),
    ...optionalTypedField(
      'attention',
      optionalBoolean(input.attention, 'attention'),
    ),
    ...optionalTypedField(
      'groupBy',
      optionalChoice(
        input.groupBy,
        MANAGEMENT_TIMELINE_GROUP_BY_VALUES,
        'groupBy',
      ) as ManagementTimelineGroupBy | undefined,
    ),
    ...optionalStringField(input, 'groupKey'),
  };
}

export function serializeManagementTimelineQuery(
  query: ManagementTimelineQuery,
): SerializedManagementTimelineQuery {
  const normalized = parseManagementTimelineQuery(query);
  const serialized: SerializedManagementTimelineQuery = {};
  const keys: Array<keyof ManagementTimelineQuery> = [
    'view',
    'participant',
    'skill',
    'task',
    'outcome',
    'type',
    'from',
    'to',
    'attention',
    'groupBy',
    'groupKey',
    'page',
    'limit',
  ];
  for (const key of keys) {
    if (query[key] !== undefined) serialized[key] = String(normalized[key]);
  }
  return serialized;
}

function optionalStringField<
  K extends 'participant' | 'skill' | 'task' | 'groupKey',
>(
  input: ManagementTimelineQueryInput | ManagementTimelineQuery,
  key: K,
): Partial<Pick<ManagementTimelineQuery, K>> {
  return optionalTypedField(key, optionalString(input[key], key));
}

function optionalTypedField<K extends keyof ManagementTimelineQuery>(
  key: K,
  value: ManagementTimelineQuery[K],
): Partial<Pick<ManagementTimelineQuery, K>> {
  return value === undefined
    ? {}
    : ({ [key]: value } as Pick<ManagementTimelineQuery, K>);
}

function optionalString(value: unknown, label: string): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string' || !value.trim()) invalid(label);
  return value.trim();
}

function optionalTimestamp(value: unknown, label: string): string | undefined {
  const timestamp = optionalString(value, label);
  if (timestamp !== undefined && !Number.isFinite(Date.parse(timestamp))) {
    invalid(label);
  }
  return timestamp;
}

function optionalBoolean(value: unknown, label: string): boolean | undefined {
  if (value === undefined) return undefined;
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return invalid(label);
}

function optionalChoice<T extends string>(
  value: unknown,
  allowed: readonly T[],
  label: string,
): T | undefined {
  const choice = optionalString(value, label);
  if (choice === undefined) return undefined;
  if (!allowed.includes(choice as T)) invalid(label);
  return choice as T;
}

function positiveInteger(
  value: unknown,
  label: string,
  fallback: number,
  maximum: number,
): number {
  if (value === undefined) return fallback;
  const parsed =
    typeof value === 'number' ? value : strictIntegerString(value, label);
  if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > maximum) {
    invalid(label);
  }
  return parsed;
}

function strictIntegerString(value: unknown, label: string): number {
  const text = optionalString(value, label);
  if (text === undefined || !/^\d+$/.test(text)) invalid(label);
  return Number(text);
}

function invalid(label: string): never {
  throw new CockpitContractError(
    'COCKPIT_INVALID_QUERY',
    `Invalid management timeline query field: ${label}.`,
    'Correct the query parameters and retry.',
  );
}
